import { ConversationProvider, useConversation } from '@elevenlabs/react';
import type React from 'react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { getVoiceMessages, type VoiceMessages } from '../config/voiceMessages';
import { resolveWrapUpDelayMs } from '../config/voiceWrapUp';
import { ConfigContext } from '../context/ConfigContext';
import { useVoiceWrapUpNotice } from '../hooks/useVoiceWrapUpNotice';

type VoicePanelProps = {
  agentId: string;
  // Called whenever the active-call state changes so the parent can guard
  // tab switches while a call is in progress.
  onCallStatusChange?: (isActive: boolean) => void;
};

type VoicePanelContentProps = {
  onCallStatusChange?: (isActive: boolean) => void;
};

type VoiceErrorType =
  | 'permission-denied'
  | 'no-device'
  | 'device-in-use'
  | 'connection'
  | 'generic';

type VoiceError = {
  type: VoiceErrorType;
  message: string;
  // Optional hint shown below the main message to help the user resolve it.
  hint?: string;
};

// Maps an SDK error message string to a structured, user-friendly VoiceError.
// Connection errors always show the firewall message because any network-level
// block (ElevenLabs or LiveKit) requires the same fix: allow the domains.
const categorizeError = (
  message: string,
  messages: VoiceMessages,
): VoiceError => {
  const lower = message.toLowerCase();

  if (
    lower.includes('notallowederror') ||
    lower.includes('permission denied') ||
    lower.includes('permission') ||
    lower.includes('not allowed')
  ) {
    return {
      type: 'permission-denied',
      ...messages.errors.permissionDenied,
    };
  }

  if (
    lower.includes('notfounderror') ||
    lower.includes('no device') ||
    lower.includes('device not found') ||
    lower.includes('could not start')
  ) {
    return { type: 'no-device', ...messages.errors.noDevice };
  }

  if (
    lower.includes('notreadableerror') ||
    lower.includes('in use') ||
    lower.includes('hardware error')
  ) {
    return { type: 'device-in-use', ...messages.errors.deviceInUse };
  }

  if (
    lower.includes('network') ||
    lower.includes('connection') ||
    lower.includes('websocket') ||
    lower.includes('signal') ||
    lower.includes('unreachable')
  ) {
    // Both ElevenLabs and LiveKit traffic must be allowed for voice to work.
    // A connection failure at either stage has the same root cause and fix.
    return { type: 'connection', ...messages.errors.connection };
  }

  return { type: 'generic', ...messages.errors.generic };
};

// Checks the browser microphone permission state before attempting a session.
// Only uses the Permissions API (fast pre-check for the already-denied case).
// Returns a VoiceError if microphone is blocked, otherwise null.
const checkMicrophonePermission = async (
  messages: VoiceMessages,
): Promise<VoiceError | null> => {
  if (!navigator.permissions) {
    return null;
  }

  try {
    const permission = await navigator.permissions.query({
      name: 'microphone' as PermissionName,
    });

    if (permission.state === 'denied') {
      return {
        type: 'permission-denied',
        ...messages.errors.permissionBlocked,
      };
    }
  } catch {
    // Some browsers throw when querying 'microphone' — treat as unknown, proceed.
  }

  return null;
};

const getOrbModifier = (isConnected: boolean, isSpeaking: boolean): string => {
  if (!isConnected) {
    return '';
  }
  if (isSpeaking) {
    return 'voice-panel-orb--speaking';
  }
  return 'voice-panel-orb--listening';
};

const getWaveformModifier = (
  isConnected: boolean,
  isSpeaking: boolean,
): string => {
  if (!isConnected) {
    return 'voice-panel-waveform--idle';
  }
  if (isSpeaking) {
    return 'voice-panel-waveform--speaking';
  }
  return 'voice-panel-waveform--listening';
};

// Inner component that consumes conversation state — must live inside ConversationProvider.
const VoicePanelContent: React.FC<VoicePanelContentProps> = ({
  onCallStatusChange,
}) => {
  const {
    scheme: {
      header: { headerColor },
    },
    headerConfig: { logo, logoType },
    voice,
  } = useContext(ConfigContext);

  const messages = getVoiceMessages(voice?.language);

  const [voiceError, setVoiceError] = useState<VoiceError | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // * Set from the SDK onConnect callback, so each new call gets a fresh value
  // * and re-arms the wrap-up timer from its own connect moment.
  const [connectedAt, setConnectedAt] = useState<number | null>(null);

  const { startSession, endSession, status, isSpeaking, sendContextualUpdate } =
    useConversation({
      onConnect: () => {
        setConnectedAt(Date.now());
      },
      onDisconnect: () => {
        setConnectedAt(null);
      },
      onError: (message) => {
        console.error('[VoicePanel] Conversation error:', message);
        setVoiceError(categorizeError(message, messages));
      },
    });

  // Keep a ref to endSession so the unmount cleanup always calls the latest version.
  const endSessionRef = useRef(endSession);
  endSessionRef.current = endSession;

  // End the active call when the Voice panel unmounts (user switched to Chat tab).
  useEffect(() => {
    return () => {
      endSessionRef.current();
    };
    // Intentionally empty — we only want this to fire on unmount
  }, []);

  // Notify the parent whenever the call becomes active or inactive so it can
  // guard tab switches while a call is in progress.
  useEffect(() => {
    onCallStatusChange?.(status === 'connecting' || status === 'connected');
  }, [status, onCallStatusChange]);

  const handleStartCall = useCallback(async () => {
    // Clear any previous error before attempting a new session.
    setVoiceError(null);

    // Fast pre-check via the Permissions API: if microphone was already denied
    // from a previous session, show the blocked error without prompting again.
    const permissionError = await checkMicrophonePermission(messages);
    if (permissionError !== null) {
      setVoiceError(permissionError);
      return;
    }

    // Request microphone access directly before handing off to the SDK.
    // The ElevenLabs SDK does not propagate NotAllowedError to onError in
    // non-Chromium browsers (Safari, Firefox) — it silently resets status to
    // 'disconnected'. Calling getUserMedia here catches the denial synchronously
    // in every browser. We stop the test stream immediately; startSession will
    // re-request internally and will not show a second prompt since permission
    // was just granted.
    try {
      const testStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      testStream.getTracks().forEach((track) => track.stop());
    } catch (micError) {
      console.error('[VoicePanel] Microphone access denied:', micError);
      const message =
        micError instanceof Error ? micError.message : String(micError);
      setVoiceError(categorizeError(message, messages));
      return;
    }

    try {
      startSession();
    } catch (sessionError) {
      console.error('[VoicePanel] Failed to start session:', sessionError);
      const message =
        sessionError instanceof Error
          ? sessionError.message
          : String(sessionError);
      setVoiceError(categorizeError(message, messages));
    }
  }, [startSession, messages]);

  const handleEndCall = useCallback(() => {
    try {
      endSession();
    } catch (error) {
      console.error('[VoicePanel] Failed to end session:', error);
    }
  }, [endSession]);

  const isDisconnected = status === 'disconnected';
  const isConnecting = status === 'connecting';
  const isConnected = status === 'connected';
  const isError = status === 'error';
  // Show the Start call button when idle or after an error.
  const showStartButton = isDisconnected || isError;

  useVoiceWrapUpNotice({
    isConnected,
    connectedAt,
    delayMs: resolveWrapUpDelayMs(voice?.wrapUpAtSeconds),
    noticeText: messages.wrapUpNotice,
    sendContextualUpdate,
  });

  const orbModifier = getOrbModifier(isConnected, isSpeaking);
  const waveformModifier = getWaveformModifier(isConnected, isSpeaking);

  return (
    <div className='voice-panel'>
      {/* Avatar orb — grows a pulsing ring when the call is active */}
      <div className={`voice-panel-orb ${orbModifier}`}>
        <div
          className='voice-panel-orb-ring'
          style={{ borderColor: headerColor }}
        />
        <div
          className='voice-panel-orb-inner'
          style={{ backgroundColor: isConnected ? headerColor : '#f0f0f0' }}>
          {logo && (
            <img
              src={logo}
              alt={messages.avatarAlt}
              className={`voice-panel-logo${logoType === 'circle' ? ' voice-panel-logo--circle' : ''}`}
            />
          )}
        </div>
      </div>

      {/* Animated waveform bars — reflect speaking state */}
      <div className={`voice-panel-waveform ${waveformModifier}`}>
        {[0, 1, 2, 3, 4].map((barIndex) => (
          <div
            key={barIndex}
            className='voice-panel-waveform-bar'
            style={{ backgroundColor: headerColor }}
          />
        ))}
      </div>

      {/* Human-readable status line */}
      <p className='voice-panel-status-text'>
        {isDisconnected && !voiceError && messages.status.idle}
        {isConnecting && messages.status.connecting}
        {isConnected &&
          (isSpeaking ? messages.status.speaking : messages.status.listening)}
        {isError && !voiceError && messages.status.connectionFailed}
      </p>

      {/* Structured error block — shown when a categorised error is available */}
      {voiceError !== null && (
        <div className='voice-panel-error'>
          <span className='voice-panel-error-message'>
            {voiceError.message}
          </span>
          {voiceError.hint && (
            <span className='voice-panel-error-hint'>{voiceError.hint}</span>
          )}
        </div>
      )}

      {/* Call control button */}
      <div className='voice-panel-controls'>
        {showStartButton && (
          <button
            type='button'
            className='voice-panel-button voice-panel-button--start'
            style={{ backgroundColor: headerColor }}
            onClick={handleStartCall}
            disabled={!isOnline}
            title={!isOnline ? messages.buttons.offlineTitle : undefined}>
            {isError || voiceError !== null
              ? messages.buttons.tryAgain
              : messages.buttons.startCall}
          </button>
        )}
        {isConnecting && (
          <button
            type='button'
            className='voice-panel-button voice-panel-button--connecting'
            disabled>
            {messages.buttons.connecting}
          </button>
        )}
        {isConnected && (
          <button
            type='button'
            className='voice-panel-button voice-panel-button--end'
            onClick={handleEndCall}>
            {messages.buttons.endCall}
          </button>
        )}
      </div>
    </div>
  );
};

// Outer component that sets up the conversation provider with agent config.
const VoicePanel: React.FC<VoicePanelProps> = ({
  agentId,
  onCallStatusChange,
}) => {
  return (
    <ConversationProvider agentId={agentId} serverLocation='eu-residency'>
      <VoicePanelContent onCallStatusChange={onCallStatusChange} />
    </ConversationProvider>
  );
};

export default VoicePanel;
